import { filterDOMProps } from '@react-aria/utils';
import type { ForwardedRef, ReactElement } from 'react';
import { forwardRef } from 'react';
import { useSlottedContext } from 'react-aria-components';

import { cn, useContextProps } from '@infinex/ui-styles';

import { IconContext } from './context';
import type { IconProps } from './types';

import { VIEW_BOX } from '../constants';

/**
 * @private Only for internal use.
 *
 * A reusable wrapper for SVG icons. The component provides basic styling and
 * hooks into slotted context for rending within a slotted component.
 */
export const Icon = forwardRef(function Icon(
  props: IconProps & { children: ReactElement },
  ref: ForwardedRef<SVGSVGElement>
) {
  [props, ref] = useContextProps(props, ref, IconContext);
  const ctx = useSlottedContext(IconContext);

  if (props.isHidden) {
    return null;
  }

  const { fill, size, className, style, ...otherProps } = props;
  const hasAriaLabel = props['aria-label'] || props['aria-labelledby'];

  const svg = (
    <svg
      {...filterDOMProps(otherProps, { labelable: true })}
      ref={ref}
      aria-hidden={hasAriaLabel ? undefined : true}
      fill="currentColor"
      focusable="false"
      role="img"
      viewBox={VIEW_BOX}
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        'shrink-0 grow-0',
        getSizeClasses(size),
        {
          'fill-default': fill === 'default',
          'fill-emphasis': fill === 'emphasis',
          'fill-secondary': fill === 'secondary',
          'fill-tertiary': fill === 'tertiary',
          'fill-disabled': fill === 'disabled',
          'fill-placeholder': fill === 'placeholder',
          'fill-inverse': fill === 'inverse',
          'fill-brand': fill === 'brand',
          'fill-brandOnFill': fill === 'brandOnFill',
          'fill-caution': fill === 'caution',
          'fill-cautionOnFill': fill === 'cautionOnFill',
          'fill-critical': fill === 'critical',
          'fill-criticalOnFill': fill === 'criticalOnFill',
          'fill-highlight': fill === 'highlight',
          'fill-highlightOnFill': fill === 'highlightOnFill',
          'fill-info': fill === 'info',
          'fill-infoOnFill': fill === 'infoOnFill',
          'fill-positive': fill === 'positive',
          'fill-positiveOnFill': fill === 'positiveOnFill',
          'fill-bull': fill === 'bull',
          'fill-bullSecondary': fill === 'bullSecondary',
          'fill-bear': fill === 'bear',
          'fill-bearSecondary': fill === 'bearSecondary',
          'fill-patronage': fill === 'patronage',
        },
        className
      )}
      style={style}
      // @ts-expect-error - arbitrary attribute on SVG
      slot={props.slot || 'icon'}
    >
      {props.children}
    </svg>
  );

  if (ctx?.render) {
    return ctx.render(svg);
  }

  return svg;
});

function getSizeClasses(size: IconProps['size'] = 'small') {
  if (typeof size === 'string') {
    return {
      'size-2xsmall': size === '2xsmall',
      'size-xsmall': size === 'xsmall',
      'size-small': size === 'small',
      'size-medium': size === 'medium',
      'size-large': size === 'large',
      'size-xlarge': size === 'xlarge',
    };
  }

  return {
    'size-2xsmall': size.base === '2xsmall',
    'xs:size-2xsmall': size.xs === '2xsmall',
    'sm:size-2xsmall': size.sm === '2xsmall',
    'md:size-2xsmall': size.md === '2xsmall',
    'lg:size-2xsmall': size.lg === '2xsmall',
    'xl:size-2xsmall': size.xl === '2xsmall',
    '2xl:size-2xsmall': size['2xl'] === '2xsmall',

    'size-xsmall': size.base === 'xsmall',
    'xs:size-xsmall': size.xs === 'xsmall',
    'sm:size-xsmall': size.sm === 'xsmall',
    'md:size-xsmall': size.md === 'xsmall',
    'lg:size-xsmall': size.lg === 'xsmall',
    'xl:size-xsmall': size.xl === 'xsmall',
    '2xl:size-xsmall': size['2xl'] === 'xsmall',

    'size-small': size.base === 'small',
    'xs:size-small': size.xs === 'small',
    'sm:size-small': size.sm === 'small',
    'md:size-small': size.md === 'small',
    'lg:size-small': size.lg === 'small',
    'xl:size-small': size.xl === 'small',
    '2xl:size-small': size['2xl'] === 'small',

    'size-medium': size.base === 'medium',
    'xs:size-medium': size.xs === 'medium',
    'sm:size-medium': size.sm === 'medium',
    'md:size-medium': size.md === 'medium',
    'lg:size-medium': size.lg === 'medium',
    'xl:size-medium': size.xl === 'medium',
    '2xl:size-medium': size['2xl'] === 'medium',

    'size-large': size.base === 'large',
    'xs:size-large': size.xs === 'large',
    'sm:size-large': size.sm === 'large',
    'md:size-large': size.md === 'large',
    'lg:size-large': size.lg === 'large',
    'xl:size-large': size.xl === 'large',
    '2xl:size-large': size['2xl'] === 'large',

    'size-xlarge': size.base === 'xlarge',
    'xs:size-xlarge': size.xs === 'xlarge',
    'sm:size-xlarge': size.sm === 'xlarge',
    'md:size-xlarge': size.md === 'xlarge',
    'lg:size-xlarge': size.lg === 'xlarge',
    'xl:size-xlarge': size.xl === 'xlarge',
    '2xl:size-xlarge': size['2xl'] === 'xlarge',
  };
}
